import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../Components/SnackBar";
import {
  apiGetRequest,
  apiPostRequest,
  apiDeleteRequest,
  apiPutRequest,
} from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { API_RECORDS_COUNT } from "../../../../Constants/config";
import ProductsTable from "./ProductsTable";
import ProductModal from "./ProductModal";
import DeleteProductDialog from "./DeleteProductDialog";
import PauseProductDialog from "./PauseProductDialog";
import FulfillmentReminderDialog from "./FulfillmentReminderDialog";
import {
  PRODUCT_ERROR_CODE,
  PRODUCT_STATUS,
  getProductErrorCode,
  getProductStatus,
  isDraftProduct,
} from "../../../../Constants/productStatus";
import {
  draftCountLineStyle,
  draftFilterRowStyle,
  draftFilterChipStyle,
  productsTableContainerStyle,
  productsHeaderStyle,
  productsHeaderLeftStyle,
  myProductsTitleStyle,
  addProductButtonStyle,
  searchFieldStyle,
  emptyStateBoxStyle,
  emptyStateTitleStyle,
  emptyStateDescriptionStyle,
} from "./styles";

const PAGE_SIZE = API_RECORDS_COUNT;

/**
 * Supplier catalog manager. Serves both verticals — stores (`products`) and
 * restaurant menus (`restaurants`) — because the BE exposes the same endpoints
 * for each. Only the endpoint segment differs; see endpoints.catalog().
 */
const ProductsContent = ({ vertical = "products" }) => {
  // Memoized so the fetch hooks below can depend on it without re-running on
  // every render — it only changes when the vertical does.
  const api = useMemo(() => endpoints.catalog(vertical), [vertical]);
  const { t } = useTranslation(["supplier-products", "categories"]);
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  // false | "created" | "published". Opens when an item just went live (a
  // create with a photo, a published draft, an unpause) and the BE flags
  // `fulfillment_missing`: the supplier has neither pickup nor any delivery
  // district configured, so the item is invisible to members. The dialog
  // nudges them straight to /business-settings.
  const [showFulfillmentReminder, setShowFulfillmentReminder] = useState(false);

  // Products data
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modals
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [duplicatingProduct, setDuplicatingProduct] = useState(null);
  // The modal was opened from "Add a photo to publish": it scrolls to the
  // photo field, and a paused item is unpaused once the photo is saved.
  const [photoIntent, setPhotoIntent] = useState(false);

  // Every draft of the supplier in this catalog, counted by the server (the
  // list is paginated, so the loaded rows can't tell). null = not reported.
  const [draftCount, setDraftCount] = useState(null);
  // "Show drafts only": the server filters (`status=draft`), so it works
  // across every page, not just the loaded rows.
  const [draftsOnly, setDraftsOnly] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);

  // Pause dialog
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [pausingProduct, setPausingProduct] = useState(null);
  const [pauseLoader, setPauseLoader] = useState(false);

  // Categories for the modal
  const [categories, setCategories] = useState([]);

  const loadMoreSentinelRef = useRef(null);

  // Cap the products scroll region so its bottom lands at the viewport
  // bottom: the list then scrolls internally and the page/window never
  // grows a second "big" scrollbar. We measure at runtime because the
  // shared supplier layout isn't a bounded flex column, so a static
  // height can't know how much chrome sits above this table.
  const scrollRegionRef = useRef(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState(undefined);

  useLayoutEffect(() => {
    const el = scrollRegionRef.current;
    if (!el) return;
    const recompute = () => {
      // Document-relative top (stable regardless of any transient scroll).
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Leave a small gap so the last row clears the fixed footer.
      const FOOTER_GAP = 32;
      setScrollMaxHeight(
        `calc(100dvh - ${Math.ceil(top)}px - ${FOOTER_GAP}px)`
      );
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, []);

  // Debounce search
  useEffect(() => {
    if (searchTerm.length === 0) {
      setDebouncedSearch("");
      return;
    }
    if (searchTerm.length < 2) return;

    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Fetch products
  const fetchProducts = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);

        const params = new URLSearchParams();
        params.append("page", pageNum);
        params.append("page_size", PAGE_SIZE);
        if (debouncedSearch) params.append("search", debouncedSearch);
        if (draftsOnly) params.append("status", PRODUCT_STATUS.DRAFT);
        const response = await apiGetRequest(
          `${api.supplierItems}?${params.toString()}`
        );

        if (response.status === 200) {
          const newProducts = response.data.products || [];
          if (append) {
            setProducts((prev) => [...prev, ...newProducts]);
          } else {
            setProducts(newProducts);
          }
          setHasMore(newProducts.length === PAGE_SIZE);
          if (pageNum === 1 && typeof response.data.draft_count === "number") {
            setDraftCount(response.data.draft_count);
          }
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, draftsOnly, api]
  );

  // Reload the list from the first page (the item the supplier acted on is
  // gone, so the loaded rows are stale). Also refreshes draft_count.
  const refreshList = () => {
    setPage(1);
    fetchProducts(1, false);
  };

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiGetRequest(api.getCategories);
        if (response.status === 200) {
          setCategories(response.data.categories || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [api]);

  // Re-fetch when the search or the drafts filter changes
  useEffect(() => {
    setPage(1);
    fetchProducts(1, false);
  }, [debouncedSearch, fetchProducts]);

  // Infinite scroll via IntersectionObserver — fires when the sentinel
  // becomes visible regardless of which container is scrolling.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchProducts(nextPage, true);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [page, hasMore, loadingMore, loading, fetchProducts]);

  // Actions
  const handleAddProduct = () => {
    setEditingProduct(null);
    setDuplicatingProduct(null);
    setPhotoIntent(false);
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setDuplicatingProduct(null);
    setPhotoIntent(false);
    setShowProductModal(true);
  };

  // "Add a photo to publish" / "Add photo and publish": the edit modal, on
  // the photo field.
  const handleAddPhoto = (product) => {
    setEditingProduct(product);
    setDuplicatingProduct(null);
    setPhotoIntent(true);
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setEditingProduct(null);
    setDuplicatingProduct(null);
    setPhotoIntent(false);
  };

  const handleDuplicateProduct = (product) => {
    setDuplicatingProduct(product);
    setEditingProduct(null);
    setPhotoIntent(false);
    setShowProductModal(true);
  };

  // Keeps the server's draft count in step with what this page just did, so
  // the header line doesn't need a refetch.
  const adjustDraftCount = (delta) => {
    setDraftCount((prev) =>
      typeof prev === "number" ? Math.max(0, prev + delta) : prev
    );
  };

  // A status write the server refused, in words the supplier can act on.
  // Returns true when it was handled.
  const explainStatusError = (error, product) => {
    const code = getProductErrorCode(error);
    if (code === PRODUCT_ERROR_CODE.IMAGE_REQUIRED) {
      // Going live needs a photo — take them straight to it.
      snackbar(t("errors.image_required_to_publish"), "error");
      if (product) handleAddPhoto(product);
      return true;
    }
    if (code === PRODUCT_ERROR_CODE.DRAFT_CANNOT_PAUSE) {
      snackbar(t("errors.draft_cannot_pause"), "error");
      return true;
    }
    if (error?.response?.status === 404) {
      // Deleted elsewhere (another tab, the agent): the list is stale.
      snackbar(t("errors.not_found"), "error");
      refreshList();
      return true;
    }
    return false;
  };

  /**
   * PUT the item's status and patch it into the list. Shows the fulfillment
   * reminder when the item just went live and the supplier has no way to
   * deliver yet. Throws what the request threw.
   */
  const writeItemStatus = async (product, newStatus) => {
    const response = await apiPutRequest(api.updateItemStatus(product.id), {
      status: newStatus,
    });
    if (response.status !== 200) return false;
    const wasDraft = getProductStatus(product) === PRODUCT_STATUS.DRAFT;
    const nowActive = newStatus === PRODUCT_STATUS.ACTIVE;
    setProducts((prev) =>
      prev.map((p) =>
        p.id === product.id
          ? { ...p, is_active: nowActive, status: newStatus }
          : p
      )
    );
    if (wasDraft && nowActive) adjustDraftCount(-1);
    if (nowActive && response.data?.fulfillment_missing === true) {
      setShowFulfillmentReminder("published");
    }
    return true;
  };

  const handleDeleteClick = (product) => {
    setDeletingProduct(product);
    setShowDeleteDialog(true);
  };

  const handlePauseToggle = (product) => {
    setPausingProduct(product);
    setShowPauseDialog(true);
  };

  const confirmPauseToggle = async () => {
    if (!pausingProduct) return;
    const newStatus =
      getProductStatus(pausingProduct) === PRODUCT_STATUS.ACTIVE
        ? PRODUCT_STATUS.PAUSED
        : PRODUCT_STATUS.ACTIVE;
    const product = pausingProduct;
    const closeDialog = () => {
      setShowPauseDialog(false);
      setPausingProduct(null);
    };
    try {
      setPauseLoader(true);
      if (await writeItemStatus(product, newStatus)) {
        snackbar(
          t(newStatus === PRODUCT_STATUS.PAUSED ? "pause_success" : "unpause_success"),
          "success"
        );
        closeDialog();
      }
    } catch (error) {
      console.error("Error toggling product status:", error);
      closeDialog();
      if (!explainStatusError(error, product)) {
        snackbar(t("errors.status_failed"), "error");
      }
    } finally {
      setPauseLoader(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingProduct) return;
    try {
      setDeleteLoader(true);
      const response = await apiDeleteRequest(
        api.deleteItem(deletingProduct.id)
      );
      if (response.status === 200) {
        snackbar(t("delete_success"), "success");
        if (getProductStatus(deletingProduct) === PRODUCT_STATUS.DRAFT) {
          adjustDraftCount(-1);
        }
        setProducts((prev) => prev.filter((p) => p.id !== deletingProduct.id));
        setShowDeleteDialog(false);
        setDeletingProduct(null);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      // BE returns 409 + message "PRODUCT_HAS_ORDERS" when the product
      // is referenced by past order line items. We can't hard-delete
      // without destroying order history, so the right path is for the
      // supplier to pause the product instead. Show a clear toast that
      // explains, and close the delete dialog so they're not stuck.
      const beCode =
        getProductErrorCode(error) || error?.response?.data?.message;
      if (beCode === "PRODUCT_HAS_ORDERS") {
        snackbar(t("delete_dialog.has_orders_error"), "error");
        setShowDeleteDialog(false);
        setDeletingProduct(null);
      } else if (error?.response?.status === 404) {
        // Already gone: close the dialog and show the current list.
        snackbar(t("errors.not_found"), "error");
        setShowDeleteDialog(false);
        setDeletingProduct(null);
        refreshList();
      } else {
        snackbar(t("modal.errors.save_failed"), "error");
      }
    } finally {
      setDeleteLoader(false);
    }
  };

  const handleProductSaved = async (
    savedProduct,
    isEdit,
    { fulfillmentMissing = false, published = false, savedAsDraft = false } = {}
  ) => {
    // A product with existing orders is versioned on edit: the BE marks the
    // old row DELETED and returns a NEW successor row with a different id. Swap
    // by the id we were editing, not the returned id, so the old row is
    // replaced (and not duplicated) in the list.
    const editedId = editingProduct?.id;
    const editedProduct = editingProduct;
    const wasPhotoIntent = photoIntent;

    closeProductModal();

    // The modal only reports it when the save put the item live (a create
    // with a photo, or a published draft). Shows the reminder dialog right
    // after the product modal closes, layered on top of the products list.
    if (fulfillmentMissing === true) {
      setShowFulfillmentReminder(published ? "published" : "created");
    }

    // Keep the "N drafts" line honest without a refetch.
    if (!isEdit && savedAsDraft) adjustDraftCount(1);
    if (isEdit && published) adjustDraftCount(-1);

    if (savedProduct) {
      if (isEdit) {
        // Replace the edited row (matched by the id we opened the modal with,
        // since versioning may have changed it) with the returned product.
        setProducts((prev) =>
          prev.map((p) => (p.id === editedId ? savedProduct : p))
        );
      } else {
        // Prepend the new product to the list
        setProducts((prev) => [savedProduct, ...prev]);
      }

      if (typeof savedProduct.tags === "string" && savedProduct.tags) {
        const savedTags = savedProduct.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        if (savedTags.length) {
          setTagSuggestions((prev) =>
            Array.from(new Set([...prev, ...savedTags]))
          );
        }
      }
    }

    // "Add a photo to publish" on a PAUSED item: the edit kept it paused (an
    // edit never changes a live/paused item's status), so finish the job the
    // button promised and make it live. A draft was already published by the
    // modal itself (`publish=true`).
    if (
      isEdit &&
      wasPhotoIntent &&
      savedProduct &&
      getProductStatus(editedProduct) === PRODUCT_STATUS.PAUSED &&
      getProductStatus(savedProduct) === PRODUCT_STATUS.PAUSED
    ) {
      // The save itself succeeded with a valid photo (the server refuses a
      // live/paused item without one), so whatever happens next the row has
      // its photo and "Unpause" is available from the menu.
      const markPhotoSaved = () =>
        setProducts((prev) =>
          prev.map((p) =>
            p.id === savedProduct.id ? { ...p, has_image: true } : p
          )
        );
      let unpaused = false;
      try {
        unpaused = await writeItemStatus(savedProduct, PRODUCT_STATUS.ACTIVE);
      } catch (error) {
        console.error("Error publishing product after adding a photo:", error);
        if (error?.response?.status === 404) {
          explainStatusError(error, null);
          return;
        }
      }
      if (unpaused) {
        // Covers both steps: the photo was saved and the item is live.
        snackbar(t("draft.published_success"), "success");
      } else {
        markPhotoSaved();
        snackbar(t("errors.photo_saved_still_paused"), "error");
      }
    }
  };

  // "Publish" on a draft that already has its photo: no modal needed.
  const handlePublishDraft = async (product) => {
    try {
      if (await writeItemStatus(product, PRODUCT_STATUS.ACTIVE)) {
        snackbar(t("draft.published_success"), "success");
      } else {
        snackbar(t("errors.status_failed"), "error");
      }
    } catch (error) {
      console.error("Error publishing draft:", error);
      if (!explainStatusError(error, product)) {
        snackbar(t("errors.status_failed"), "error");
      }
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  const [tagSuggestions, setTagSuggestions] = useState([]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await apiGetRequest(api.getAllTags);
        if (response.status === 200) {
          setTagSuggestions(response.data.tags || []);
        }
      } catch (error) {
        console.error("Error fetching product tags:", error);
      }
    };
    fetchTags();
  }, [api]);

  // With the drafts filter on, a draft published from this view leaves it.
  const visibleProducts = draftsOnly
    ? products.filter(isDraftProduct)
    : products;
  const noRows = !loading && visibleProducts.length === 0;
  const isSearchEmpty = noRows && debouncedSearch;
  const isDraftsEmpty = noRows && !debouncedSearch && draftsOnly;
  const isEmpty = noRows && !debouncedSearch && !draftsOnly;

  return (
    <Box sx={productsTableContainerStyle}>
      {/* Header: My Products + Add button + Search */}
      <Box sx={productsHeaderStyle}>
        <Box sx={productsHeaderLeftStyle}>
          <Typography sx={myProductsTitleStyle}>
            {vertical === "restaurants" ? t("my_dishes") : t("my_products")}
          </Typography>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            sx={addProductButtonStyle}
          >
            {vertical === "restaurants" ? t("add_new_dish") : t("add_new_product")}
          </Button>
        </Box>

        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          placeholder={t("search_placeholder")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgba(62, 63, 76, 0.4)" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={searchFieldStyle}
        />
      </Box>

      {/* Drafts are invisible to members, so say how many aren't published.
          The number is the server's (all pages), not the loaded rows. Stays
          while the filter is on, so it can always be cleared. */}
      {(draftCount > 0 || draftsOnly) && (
        <Box sx={draftFilterRowStyle}>
          {draftCount > 0 && (
            <Typography sx={draftCountLineStyle} role="status">
              {t("draft.unpublished_count", { count: draftCount })}
            </Typography>
          )}
          <Chip
            label={t("draft.show_drafts_only")}
            size="small"
            variant="outlined"
            clickable
            aria-pressed={draftsOnly}
            onClick={() => setDraftsOnly((prev) => !prev)}
            onDelete={draftsOnly ? () => setDraftsOnly(false) : undefined}
            sx={draftFilterChipStyle(draftsOnly)}
          />
        </Box>
      )}

      {/* Content — the single scroll region for this tab. Capped to the
          viewport bottom (scrollMaxHeight) so the list scrolls here instead
          of growing the page and adding a second window scrollbar. */}
      <Box
        ref={scrollRegionRef}
        className="custom-scrollbar"
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: scrollMaxHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress size={60} sx={{ color: "#248B70" }} />
          </Box>
        ) : isEmpty ? (
          /* Empty state */
          <Box sx={emptyStateBoxStyle}>
            <img
              src="/icons/add-files-icons.svg"
              alt="no products"
              width="48"
              height="48"
              style={{ opacity: 0.6, marginBottom: "16px" }}
            />
            <Typography sx={emptyStateTitleStyle}>
              {t("no_products_yet")}
            </Typography>
            <Typography sx={emptyStateDescriptionStyle}>
              {t("no_products_yet_description")}
            </Typography>
          </Box>
        ) : isSearchEmpty ? (
          /* No search results */
          <Box sx={emptyStateBoxStyle}>
            <Typography sx={emptyStateTitleStyle}>
              {t("no_search_results")}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClearSearch}
              sx={{
                ...addProductButtonStyle,
                mt: "16px",
                borderRadius: "900px",
              }}
            >
              {t("clear_search")}
            </Button>
          </Box>
        ) : isDraftsEmpty ? (
          /* Drafts filter on, nothing left to publish */
          <Box sx={emptyStateBoxStyle}>
            <Typography sx={emptyStateTitleStyle}>
              {t("draft.no_drafts")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setDraftsOnly(false)}
              sx={{
                ...addProductButtonStyle,
                mt: "16px",
                borderRadius: "900px",
              }}
            >
              {t("draft.show_all_items")}
            </Button>
          </Box>
        ) : (
          <>
            <ProductsTable
              vertical={vertical}
              products={visibleProducts}
              onEdit={handleEditProduct}
              onDuplicate={handleDuplicateProduct}
              onDelete={handleDeleteClick}
              onPauseToggle={handlePauseToggle}
              onAddPhoto={handleAddPhoto}
              onPublish={handlePublishDraft}
            />
            {hasMore && <Box ref={loadMoreSentinelRef} sx={{ height: "1px" }} />}
            {loadingMore && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: "16px",
                }}
              >
                <CircularProgress size={32} sx={{ color: "#248B70" }} />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Product Modal (Add / Edit / Duplicate) */}
      {showProductModal && (
        <ProductModal
          vertical={vertical}
          open={showProductModal}
          onClose={closeProductModal}
          focusImage={photoIntent}
          product={editingProduct || duplicatingProduct}
          isEdit={!!editingProduct}
          isDuplicate={!!duplicatingProduct}
          categories={categories}
          tagSuggestions={tagSuggestions}
          onProductSaved={handleProductSaved}
          onItemMissing={() => {
            closeProductModal();
            refreshList();
          }}
        />
      )}

      {/* Delete confirmation */}
      {showDeleteDialog && deletingProduct && (
        <DeleteProductDialog
          open={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setDeletingProduct(null);
          }}
          onConfirm={confirmDelete}
          loading={deleteLoader}
        />
      )}

      {/* Pause/Unpause confirmation */}
      {showPauseDialog && pausingProduct && (
        <PauseProductDialog
          open={showPauseDialog}
          onClose={() => {
            setShowPauseDialog(false);
            setPausingProduct(null);
          }}
          onConfirm={confirmPauseToggle}
          loading={pauseLoader}
          isActive={getProductStatus(pausingProduct) === PRODUCT_STATUS.ACTIVE}
        />
      )}

      {/* Fulfillment reminder — opens after a create when the BE flags
          that the supplier has no pickup AND no delivery districts set
          up. The product was created but isn't visible to members until
          they fix that. Single primary action: navigate to settings.
          X / backdrop / Escape all dismiss (it's a soft nudge). */}
      <FulfillmentReminderDialog
        open={Boolean(showFulfillmentReminder)}
        published={showFulfillmentReminder === "published"}
        onClose={() => setShowFulfillmentReminder(false)}
        onGoToSettings={() => {
          setShowFulfillmentReminder(false);
          navigate("/business-settings");
        }}
      />
    </Box>
  );
};

export default ProductsContent;
